import React, { Fragment, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, CircularProgress, Divider, LinearProgress, Typography } from '@mui/material';
import { DrawerLayoutWrapper, MDialog } from 'common';
import { format, parseISO } from 'date-fns';
import * as R from 'ramda';
import { DrawerContext } from 'common/drawer/DrawerContext';
import { createSnack } from 'snackbar/snackbarSlice';
import { useDispatch } from 'react-redux';
import { useCreateReconsignmentMutation } from '../reconsignmentsEndpoints';
import ConfirmationStep from './OneToOneConfirmation';

const OneToOneSummaryStep = ({
  prev,
  isInvalid,
  next,
  reconsignmentState,
  reset,
  setReconsignmentState,
  setOpenDialog,
  openDialog,
  isSuccess,
  setIsSuccess,
}) => {
  const {
    fromLocation,
    fullFilment,
    inventoryOwner,
    origin,
    product,
    rvp,
    toLocation,
    transferDate,
    volume,
  } = reconsignmentState;

  const { setDrawerComponentProps } = useContext(DrawerContext);
  const [inventoryStatusValue, setInventoryStatusValue] = useState(null);
  const [consignmentStatusValue, setConsignmentStatusValue] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false); // New state variable
  const [resultData, setResultData] = useState(null);

  const handleDrawerClose = () => {
    setOpenDialog(false);
    setDrawerComponentProps({
      Component: '',
      forceCloseSelector: () => false,
      holdOpenSelector: () => false,
      refreshNotifications: false,
      resetWizard: undefined,
      setCurrentDialogOpen: () => {},
      type: '',
    });
  };

  const handleDrawerCloseConfirm = () => {
    setOpenDialog(true);
  };

  useEffect(() => {
    if (fullFilment && fullFilment[0].label !== null) {
      if (['Any Inventory', 'Any Uncontrolled', 'Any Controlled'].includes(fullFilment[0].label)) {
        setInventoryStatusValue('ANY');
      } else if (
        ['Any Available', 'Uncontrolled Available', 'Controlled Available'].includes(
          fullFilment[0].label
        )
      ) {
        setInventoryStatusValue('AVAILABLE');
      } else if (
        ['Any In-Transit', 'Uncontrolled In-Transit', 'Controlled In-Transit'].includes(
          fullFilment[0].label
        )
      ) {
        setInventoryStatusValue('IN-TRANSIT');
      }
    }
  }, [fullFilment, setInventoryStatusValue]);

  useEffect(() => {
    if (fullFilment && fullFilment[0].label !== null) {
      if (['Any Inventory', 'Any Available', 'Any In-Transit'].includes(fullFilment[0].label)) {
        setConsignmentStatusValue('ANY');
      } else if (
        ['Any Uncontrolled', 'Uncontrolled Available', 'Uncontrolled In-Transit'].includes(
          fullFilment[0].label
        )
      ) {
        setConsignmentStatusValue('UNCONTROLLED');
      } else if (
        ['Any Controlled', 'Controlled Available', 'Controlled In-Transit'].includes(
          fullFilment[0].label
        )
      ) {
        setConsignmentStatusValue('CONTROLLED');
      }
    }
  }, [fullFilment, setConsignmentStatusValue]);

  const isGasoline = R.equals(R.T(), R.prop('isGasoline', product));
  const summaryFieldsMap = {
    Details: [
      {
        id: 1,
        label: 'Transfer Date',
        value: !R.isNil(transferDate)
          ? format(parseISO(transferDate), 'MM/dd/yyyy')
          : 'Invalid Date',
      },
      {
        id: 2,
        label: 'Affiliate',
        value: inventoryOwner?.customerNameShort,
      },
      {
        id: 3,
        label: 'Product',
        value: product?.productName,
      },
      isGasoline
        ? {
            id: 4,
            label: 'RVP',
            value: rvp,
          }
        : null,
      {
        id: 5,
        label: 'From Location',
        value: fromLocation?.locationName,
      },
      {
        id: 6,
        label: 'To Location',
        value: toLocation?.locationName,
      },
      {
        id: 7,
        label: 'Origin(s)',
        sx: { display: 'block', whiteSpace: 'pre-wrap' },
        value: origin.length > 0 ? origin.map(R.prop('originName')).join('\n') : 'Any',
      },
    ],
    TransferVolume: [
      {
        id: 8,
        label: 'Transfer Volume',
        value: `${volume} bbl`,
      },
      {
        id: 9,
        label: 'Inventory Status',
        value: inventoryStatusValue,
      },
      {
        id: 10,
        label: 'Consignment Status',
        value: consignmentStatusValue,
      },
    ],
  };

  const renderLabel = str => {
    const res = str.replace(/([A-Z])/g, ' $1');
    return res.charAt(0).toUpperCase() + res.slice(1);
  };

  const [submitCreate] = useCreateReconsignmentMutation();
  const dispatch = useDispatch();

  const handleSubmit = () => {
    setIsSubmitting(true); // Set loading state to true
    const data = {
      consignmentStatus:
        consignmentStatusValue === 'ANY' ? null : consignmentStatusValue.replace(/-/g, ''),
      fromLocation: fromLocation.locationId,
      inventoryOwner: {
        companyCustomerIdName: inventoryOwner.customerIdName,
        dataCompanyId: inventoryOwner.dataCompanyId,
      },
      inventoryStatus:
        inventoryStatusValue === 'ANY' ? null : inventoryStatusValue.replace(/-/g, ''),
      originIds: origin.map(R.prop('originId')),
      productId: product.productId,
      rvp: rvp === 'Any' ? null : rvp,
      toLocation: toLocation.locationId,
      transferDate,
      volume: parseFloat(volume.replace(/,/g, '')), // Remove commas before parsing
    };

    submitCreate({ body: data })
      .then(res => {
        if (res.error) {
          dispatch(
            createSnack({
              message: res.error.data.title,
              options: { variant: 'error' },
            })
          );
        } else {
          setResultData(res.data); // Save result data
          setIsSuccess(true);
        }
      })
      .finally(() => {
        setIsSubmitting(false); // Set loading state to false
      });
  };

  if (isSuccess) {
    // Render ConfirmationStep if submission is successful
    return (
      <ConfirmationStep
        handleDrawerClose={handleDrawerClose}
        next={next}
        onClose={handleDrawerClose}
        prev={prev}
        reconsignmentState={reconsignmentState}
        reset={reset}
        result={resultData}
        setReconsignmentState={setReconsignmentState}
      />
    );
  }

  const actionButtons = (
    <Box sx={{ display: 'flex', justifyContent: 'space-around', width: 1 }}>
      <Button onClick={prev} sx={{ width: 150 }} variant="outlined">
        Back
      </Button>
      <Button disabled={isInvalid || isSubmitting} onClick={handleSubmit} sx={{ width: 150 }}>
        {isSubmitting ? <CircularProgress size={24} /> : 'Submit'}
      </Button>
    </Box>
  );

  return (
    <DrawerLayoutWrapper
      buttons={actionButtons}
      headerButtonText="CLOSE"
      onHeaderButtonClick={handleDrawerCloseConfirm}
      title="New 1 to 1 Reconsignment"
    >
      <Box
        sx={{
          borderBottom: 1,
          borderBottomColor: 'graphic.light',
          p: 3,
          width: '100%',
        }}
      >
        <LinearProgress data-variant="magellanProgressBar" value={100} variant="determinate" />
        <Typography>Summary (3 of 3)</Typography>
      </Box>
      <Divider gap={32} />
      <Box sx={{ mt: 3, p: '0 2', pl: 2 }}>
        <Typography variant="body1">Review Your New Reconsignment</Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          mt: 3, // Add margin top to create a gap
          width: '100%',
        }}
      >
        {Object.keys(summaryFieldsMap).map((section, i) => {
          const fields = summaryFieldsMap[section];
          return (
            <Box
              key={section}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  alignContent: 'center',
                  alignItems: 'center',
                  backgroundColor: 'graphic.light',
                  display: 'flex',
                  justifyContent: 'space-between',
                  minHeight: 36,
                  pl: 3.5,
                  pr: 2,
                  width: '100%',
                }}
              >
                <Typography variant="body1">{`${i + 1}. ${renderLabel(section)}`}</Typography>
              </Box>
              {fields.filter(Boolean).map(({ id, label, value }) => (
                <Fragment key={id}>
                  <Box
                    sx={{
                      alignContent: 'center',
                      alignItems: 'center',
                      display: 'flex',
                      justifyContent: 'space-between',
                      minHeight: 48,
                      pl: 3.5,
                      pr: 2,
                      py: 1,
                      width: '100%',
                    }}
                  >
                    <Typography variant="body1">{label}</Typography>
                    <Typography
                      sx={{
                        color: 'text.primary',
                        ml: 1,
                        textAlign: 'right',
                        whiteSpace: 'pre-wrap',
                      }}
                      variant="body1"
                    >
                      {value}
                    </Typography>
                  </Box>
                </Fragment>
              ))}
            </Box>
          );
        })}
      </Box>
      <MDialog
        header="Unsaved Changes"
        leftButtonAction={() => {
          setOpenDialog(false);
          handleDrawerClose();
        }}
        leftButtonText="Continue"
        open={openDialog}
        rightButtonAction={() => setOpenDialog(false)}
        rightButtonText="Keep Editing"
      >
        <Typography color="textPrimary" component="span" variant="subtitle1">
          Are you sure you want to leave this page? You have unsaved one to one reconsignment
          changes.
        </Typography>
      </MDialog>
    </DrawerLayoutWrapper>
  );
};

OneToOneSummaryStep.propTypes = {
  isInvalid: PropTypes.bool.isRequired,
  isSuccess: PropTypes.bool.isRequired,
  next: PropTypes.func.isRequired,
  openDialog: PropTypes.bool.isRequired,
  prev: PropTypes.func.isRequired,
  reconsignmentState: PropTypes.shape({
    fromLocation: PropTypes.shape({
      locationId: PropTypes.string,
      locationName: PropTypes.string,
    }),
    fullFilment: PropTypes.shape({}),
    inventoryOwner: PropTypes.shape({
      customerIdName: PropTypes.string,
      customerNameShort: PropTypes.string,
      dataCompanyId: PropTypes.string,
    }),
    origin: PropTypes.arrayOf(),
    product: PropTypes.shape({
      productId: PropTypes.string,
      productName: PropTypes.string,
    }),
    rvp: PropTypes.string,
    toLocation: PropTypes.shape({
      locationId: PropTypes.string,
      locationName: PropTypes.string,
    }),
    transferDate: PropTypes.string,
    volume: PropTypes.string,
  }).isRequired,
  reset: PropTypes.func.isRequired,
  setIsSuccess: PropTypes.func.isRequired,
  setOpenDialog: PropTypes.func.isRequired,
  setReconsignmentState: PropTypes.func.isRequired,
};

export default OneToOneSummaryStep;
